import { MIN_DIMENSION, MAX_DIMENSION } from "../core/index.js";

export interface NewCanvasDialogOptions {
  readonly initialWidth: number;
  readonly initialHeight: number;
  readonly isDirty: boolean;
  readonly onCreate: (width: number, height: number) => void;
}

export class NewCanvasDialog {
  private overlay: HTMLElement | null = null;
  private readonly options: NewCanvasDialogOptions;

  constructor(options: NewCanvasDialogOptions) {
    this.options = options;
  }

  public open(): void {
    if (this.overlay) return;

    this.overlay = document.createElement("div");
    this.overlay.className = "modal-backdrop";
    this.overlay.setAttribute("role", "dialog");
    this.overlay.setAttribute("aria-modal", "true");
    this.overlay.setAttribute("aria-labelledby", "dialog-new-title");

    const modal = document.createElement("div");
    modal.className = "modal-window modal-window-sm";

    const header = document.createElement("div");
    header.className = "modal-header";
    header.innerHTML = `
      <h2 id="dialog-new-title" class="modal-title">Neue Leinwand</h2>
      <button type="button" class="modal-close" aria-label="Schließen">×</button>
    `;

    const body = document.createElement("div");
    body.className = "modal-body";

    if (this.options.isDirty) {
      const warnBox = document.createElement("div");
      warnBox.className = "modal-warning";
      warnBox.textContent = "Achtung: Nicht gespeicherte Änderungen gehen verloren!";
      body.appendChild(warnBox);
    }

    const dimsContainer = document.createElement("div");
    dimsContainer.className = "dimension-row form-group";
    dimsContainer.innerHTML = `
      <div class="dimension-group">
        <label for="new-width" class="field-label">Breite (px)</label>
        <input id="new-width" type="number" class="dimension-input" min="${MIN_DIMENSION}" max="${MAX_DIMENSION}" value="${this.options.initialWidth}" />
      </div>
      <span class="dimension-times">×</span>
      <div class="dimension-group">
        <label for="new-height" class="field-label">Höhe (px)</label>
        <input id="new-height" type="number" class="dimension-input" min="${MIN_DIMENSION}" max="${MAX_DIMENSION}" value="${this.options.initialHeight}" />
      </div>
    `;
    body.appendChild(dimsContainer);

    const errorBox = document.createElement("div");
    errorBox.className = "field-error";
    errorBox.style.display = "none";
    body.appendChild(errorBox);

    const footer = document.createElement("div");
    footer.className = "modal-footer";

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "button button-ghost";
    cancelBtn.textContent = "Abbrechen";
    cancelBtn.addEventListener("click", () => this.close());

    const createBtn = document.createElement("button");
    createBtn.type = "button";
    createBtn.className = "button button-primary";
    createBtn.textContent = "Erstellen";

    footer.appendChild(cancelBtn);
    footer.appendChild(createBtn);

    modal.appendChild(header);
    modal.appendChild(body);
    modal.appendChild(footer);
    this.overlay.appendChild(modal);
    document.body.appendChild(this.overlay);

    const widthInput = dimsContainer.querySelector<HTMLInputElement>("#new-width")!;
    const heightInput = dimsContainer.querySelector<HTMLInputElement>("#new-height")!;
    const closeBtn = header.querySelector<HTMLButtonElement>(".modal-close")!;

    closeBtn.addEventListener("click", () => this.close());
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.close();
    });

    createBtn.addEventListener("click", () => {
      const w = Number.parseInt(widthInput.value, 10);
      const h = Number.parseInt(heightInput.value, 10);
      if (
        !Number.isInteger(w) ||
        !Number.isInteger(h) ||
        w < MIN_DIMENSION ||
        w > MAX_DIMENSION ||
        h < MIN_DIMENSION ||
        h > MAX_DIMENSION
      ) {
        errorBox.textContent = `Breite und Höhe müssen zwischen ${MIN_DIMENSION} und ${MAX_DIMENSION} liegen.`;
        errorBox.style.display = "block";
        return;
      }
      this.options.onCreate(w, h);
      this.close();
    });

    widthInput.focus();
  }

  public close(): void {
    if (this.overlay) {
      document.body.removeChild(this.overlay);
      this.overlay = null;
    }
  }
}
